/**
 * 📅 DATE UTILITIES
 * Centralized date formatting functions for consistent display across the application
 */

export interface DateFormatOptions {
  year?: 'numeric' | '2-digit';
  month?: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow';
  day?: 'numeric' | '2-digit';
  weekday?: 'long' | 'short' | 'narrow';
  hour?: 'numeric' | '2-digit';
  minute?: 'numeric' | '2-digit';
  second?: 'numeric' | '2-digit';
  timeZone?: string;
}

/**
 * Format date with English locale for consistent display
 * @param dateString - Date string to format
 * @param options - Optional formatting options
 * @returns Formatted date string in English
 */
export const formatDate = (
  dateString: string, 
  options: DateFormatOptions = {}
): string => {
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return 'Invalid Date';
    }

    // Default formatting options (English locale)
    const defaultOptions: DateFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...options
    };

    // Format with English locale (en-US)
    return date.toLocaleDateString('en-US', defaultOptions);
  } catch (error) {
    console.error('Date formatting error:', error);
    return 'Invalid Date';
  }
};

/**
 * Format date with short format (MMM DD, YYYY)
 * @param dateString - Date string to format
 * @returns Short formatted date string
 */
export const formatDateShort = (dateString: string): string => {
  return formatDate(dateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format date with compact format (MM/DD/YYYY)
 * @param dateString - Date string to format
 * @returns Compact formatted date string
 */
export const formatDateCompact = (dateString: string): string => {
  return formatDate(dateString, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Format date with full format including weekday
 * @param dateString - Date string to format
 * @returns Full formatted date string with weekday
 */
export const formatDateFull = (dateString: string): string => {
  return formatDate(dateString, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format date with time included
 * @param dateString - Date string to format
 * @param includeSeconds - Whether to include seconds
 * @returns Formatted date and time string
 */
export const formatDateTime = (
  dateString: string, 
  includeSeconds: boolean = false
): string => {
  const timeOptions: DateFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    ...(includeSeconds && { second: '2-digit' })
  };

  return formatDate(dateString, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...timeOptions
  });
};

/**
 * Get relative time string (e.g., "2 days ago", "3 hours ago")
 * @param dateString - Date string to compare
 * @returns Relative time string
 */
export const getRelativeTime = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSeconds < 60) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`;
    if (diffMonths < 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
    return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
  } catch (error) {
    console.error('Relative time calculation error:', error);
    return 'Unknown time';
  }
};

/**
 * Check if a date is today
 * @param dateString - Date string to check
 * @returns True if the date is today
 */
export const isToday = (dateString: string): boolean => {
  try {
    const date = new Date(dateString);
    const today = new Date();
    
    return date.toDateString() === today.toDateString();
  } catch (error) {
    return false;
  }
};

/**
 * Check if a date is within the last week
 * @param dateString - Date string to check
 * @returns True if the date is within the last week
 */
export const isWithinLastWeek = (dateString: string): boolean => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    return date >= weekAgo && date <= now;
  } catch (error) {
    return false;
  }
};

/**
 * Legacy function for backward compatibility
 * @deprecated Use formatDate() instead
 */
export const formatDateVN = (dateString: string): string => {
  console.warn('formatDateVN is deprecated. Use formatDate() instead.');
  return formatDate(dateString);
}; 